/**
 * PLANTSPIRAL motion engine.
 *
 * Every scroll-linked effect shares one rAF loop. Element geometry is measured
 * in a single batch before anything is written, so a scroll frame never forces
 * a reflow; boxes are only re-read when the layout can actually have changed.
 * Everything degrades to a static page when motion is disabled or reduced.
 */
(function () {
  'use strict';

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var smallScreen = window.matchMedia('(max-width: 749px)');

  function motionAllowed() {
    return !reduceQuery.matches && !document.body.classList.contains('motion-off');
  }

  /** Guards against binding the same element twice across re-inits. */
  function bindOnce(element, key) {
    var attribute = 'motionBound' + key;
    if (element.dataset[attribute] === 'true') return false;
    element.dataset[attribute] = 'true';
    return true;
  }

  /* ----------------------------------------------------------------------
   * Shared scroll loop
   *
   * An entry is { el, update, box }. Reads happen in measureAll, writes in
   * the update callbacks, and the two never interleave.
   * -------------------------------------------------------------------- */

  var entries = [];
  var ticking = false;
  var needsMeasure = true;

  function measureAll() {
    var scrollY = window.scrollY;

    for (var i = 0; i < entries.length; i++) {
      var rect = entries[i].el.getBoundingClientRect();
      entries[i].box.top = rect.top + scrollY;
      entries[i].box.height = rect.height;
    }
  }

  function frame() {
    ticking = false;

    if (needsMeasure) {
      needsMeasure = false;
      measureAll();
    }

    var scrollY = window.scrollY;
    var viewport = window.innerHeight;

    for (var i = 0; i < entries.length; i++) {
      entries[i].update(entries[i].box, scrollY, viewport);
    }
  }

  function requestFrame() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  function requestMeasure() {
    needsMeasure = true;
    requestFrame();
  }

  /** Drops handlers whose element has been removed, e.g. by a section re-render. */
  function purge() {
    for (var i = entries.length - 1; i >= 0; i--) {
      if (!document.documentElement.contains(entries[i].el)) entries.splice(i, 1);
    }
  }

  function registerScroll(element, update) {
    purge();
    entries.push({ el: element, update: update, box: { top: 0, height: 0 } });
    requestMeasure();
  }

  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestMeasure, { passive: true });
  window.addEventListener('load', requestMeasure);

  // Images and fonts settling change every offset below them.
  if ('ResizeObserver' in window) {
    new ResizeObserver(requestMeasure).observe(document.documentElement);
  }

  /* ----------------------------------------------------------------------
   * Scroll state on the document (used by the sticky header)
   * -------------------------------------------------------------------- */

  var root = document.documentElement;

  registerScroll(root, function (box, scrollY) {
    document.body.classList.toggle('is-scrolled', scrollY > 24);
  });

  /* ----------------------------------------------------------------------
   * Line splitting for masked heading reveals
   * -------------------------------------------------------------------- */

  function splitIntoLines(element) {
    var source = element.getAttribute('data-split-source');

    if (source === null) {
      source = element.innerHTML;
      element.setAttribute('data-split-source', source);
    }

    element.innerHTML = source;

    // Wrap every word so we can group them by their vertical offset.
    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var node;

    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim().length) textNodes.push(node);
    }

    textNodes.forEach(function (textNode) {
      var fragment = document.createDocumentFragment();

      textNode.nodeValue.split(/(\s+)/).forEach(function (chunk) {
        if (!chunk.length) return;

        if (/^\s+$/.test(chunk)) {
          fragment.appendChild(document.createTextNode(chunk));
        } else {
          var word = document.createElement('span');
          word.className = 'split-word';
          word.textContent = chunk;
          fragment.appendChild(word);
        }
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    var words = Array.prototype.slice.call(element.querySelectorAll('.split-word'));
    if (!words.length) return;

    var lines = [];
    var currentTop = null;

    words.forEach(function (word) {
      var top = Math.round(word.offsetTop);

      if (currentTop === null || Math.abs(top - currentTop) > 4) {
        currentTop = top;
        lines.push([]);
      }

      lines[lines.length - 1].push(word);
    });

    var rebuilt = document.createDocumentFragment();

    lines.forEach(function (line, index) {
      var mask = document.createElement('span');
      mask.className = 'line-mask';
      mask.style.setProperty('--line-index', index);

      var inner = document.createElement('span');

      line.forEach(function (word, wordIndex) {
        if (wordIndex > 0) inner.appendChild(document.createTextNode(' '));
        inner.appendChild(document.createTextNode(word.textContent));
      });

      mask.appendChild(inner);
      rebuilt.appendChild(mask);
    });

    element.innerHTML = '';
    element.appendChild(rebuilt);
  }

  function initSplit(scope) {
    if (!motionAllowed()) return;

    var targets = scope.querySelectorAll('[data-split]');

    for (var i = 0; i < targets.length; i++) {
      splitIntoLines(targets[i]);
    }
  }

  var resizeTimer;
  window.addEventListener(
    'resize',
    function () {
      if (!motionAllowed()) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var targets = document.querySelectorAll('[data-split]');
        for (var i = 0; i < targets.length; i++) {
          var revealed = targets[i].closest('.is-revealed') || targets[i].classList.contains('is-revealed');
          splitIntoLines(targets[i]);
          if (revealed) targets[i].classList.add('is-revealed');
        }
        requestMeasure();
      }, 250);
    },
    { passive: true }
  );

  /* ----------------------------------------------------------------------
   * Reveal on scroll
   * -------------------------------------------------------------------- */

  var revealObserver = null;

  function initReveals(scope) {
    var targets = scope.querySelectorAll('[data-reveal]');

    if (!motionAllowed()) {
      for (var j = 0; j < targets.length; j++) targets[j].classList.add('is-revealed');
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (observed) {
          observed.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
      );
    }

    for (var i = 0; i < targets.length; i++) {
      // Stagger siblings that opt in through their parent.
      var group = targets[i].parentElement;

      if (group && group.hasAttribute('data-reveal-group')) {
        var step = parseInt(group.getAttribute('data-reveal-group'), 10) || 90;
        var index = Array.prototype.indexOf.call(group.children, targets[i]);
        targets[i].style.setProperty('--reveal-delay', index * step + 'ms');
      }

      revealObserver.observe(targets[i]);
    }
  }

  /* ----------------------------------------------------------------------
   * Layered parallax
   * -------------------------------------------------------------------- */

  var visible = [];
  var visibilityObserver = null;

  function watchVisibility(element) {
    if (!visibilityObserver) {
      visibilityObserver = new IntersectionObserver(
        function (observed) {
          observed.forEach(function (entry) {
            var at = visible.indexOf(entry.target);
            if (entry.isIntersecting && at === -1) visible.push(entry.target);
            if (!entry.isIntersecting && at > -1) visible.splice(at, 1);
          });
          requestMeasure();
        },
        { rootMargin: '20% 0px 20% 0px' }
      );
    }

    visibilityObserver.observe(element);
  }

  function initParallax(scope) {
    if (!motionAllowed() || document.body.dataset.parallaxEnabled === 'false') return;

    var layers = scope.querySelectorAll('[data-parallax]');

    Array.prototype.forEach.call(layers, function (layer) {
      if (!bindOnce(layer, 'Parallax')) return;

      var speed = parseFloat(layer.getAttribute('data-parallax')) || 0.15;
      watchVisibility(layer);

      registerScroll(layer, function (box, scrollY, viewport) {
        if (smallScreen.matches) {
          if (layer.style.transform) layer.style.transform = '';
          return;
        }

        if (visible.indexOf(layer) === -1) return;

        // -1 when the layer sits at the bottom of the viewport, 1 at the top.
        var progress = (box.top + box.height / 2 - scrollY - viewport / 2) / viewport;
        layer.style.transform = 'translate3d(0, ' + (-progress * speed * 100).toFixed(2) + 'px, 0)';
      });
    });
  }

  /* ----------------------------------------------------------------------
   * Scroll-drawn vine
   * -------------------------------------------------------------------- */

  function initVines(scope) {
    var vines = scope.querySelectorAll('[data-vine]');

    Array.prototype.forEach.call(vines, function (vine) {
      if (!bindOnce(vine, 'Vine')) return;

      var path = vine.querySelector('path');
      if (!path) return;

      vine.style.setProperty('--vine-length', path.getTotalLength());

      if (!motionAllowed()) {
        vine.style.setProperty('--vine-progress', 1);
        return;
      }

      registerScroll(vine, function (box, scrollY, viewport) {
        var top = box.top - scrollY;
        var total = box.height + viewport * 0.6;
        var progress = Math.min(Math.max((viewport * 0.85 - top) / total, 0), 1);

        vine.style.setProperty('--vine-progress', progress.toFixed(3));
      });
    });
  }

  /* ----------------------------------------------------------------------
   * The leaf frame
   *
   * Two things, both written as custom properties so the CSS keeps ownership
   * of the look. --canopy-open runs 0 to 1 across the first screen and the
   * stylesheet spends it on retreat and opacity; --plane-shift gives each
   * depth plane its own travel, damped so a long page cannot drag the frame
   * off the screen entirely.
   * -------------------------------------------------------------------- */

  function initCanopy(scope) {
    var canopy = scope.querySelector('[data-canopy]');
    if (!canopy || !bindOnce(canopy, 'Canopy')) return;

    /**
     * Arriving through a dense frame is the homepage moment. Elsewhere you are
     * already inside, so the frame sits at the resting state the stylesheet
     * picks and only the breeze keeps going.
     */
    if (!motionAllowed()) {
      canopy.style.setProperty('--canopy-open', 1);
      return;
    }

    if (canopy.getAttribute('data-canopy') === 'inner') return;

    var planes = Array.prototype.slice.call(canopy.querySelectorAll('[data-canopy-plane]'));
    var depths = planes.map(function (plane) {
      return parseFloat(plane.getAttribute('data-canopy-plane')) || 0.4;
    });

    registerScroll(canopy, function (box, scrollY, viewport) {
      var open = Math.min(scrollY / (viewport * 0.75), 1);
      canopy.style.setProperty('--canopy-open', open.toFixed(4));

      var travelled = Math.min(scrollY, viewport);

      for (var i = 0; i < planes.length; i++) {
        planes[i].style.setProperty(
          '--plane-shift',
          (-travelled * depths[i] * 0.08).toFixed(2) + 'px'
        );
      }
    });
  }

  /* ----------------------------------------------------------------------
   * One-shot botanical burst
   * -------------------------------------------------------------------- */

  var lastBurstAt = 0;

  function ensureBurstPortal() {
    var portal = document.querySelector('[data-leaf-burst]');
    if (portal) return portal;

    portal = document.createElement('div');
    portal.className = 'leaf-burst';
    portal.setAttribute('data-leaf-burst', '');
    portal.setAttribute('aria-hidden', 'true');
    document.body.appendChild(portal);
    return portal;
  }

  function createBurstLeaf(index, x, y, count) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    var spread = count === 1 ? 0.5 : index / (count - 1);
    var angle = (-158 + spread * 136) * (Math.PI / 180);
    var distance = 58 + (index % 4) * 12 + Math.random() * 10;
    var driftX = Math.cos(angle) * distance;
    var driftY = Math.sin(angle) * distance - 18 - Math.random() * 18;
    var spin = (index % 2 === 0 ? 1 : -1) * (42 + (index % 5) * 13);
    var size = 10 + (index % 4) * 2;

    svg.setAttribute('class', 'leaf-burst__leaf leaf-burst__leaf--tone-' + (index % 3));
    svg.setAttribute('viewBox', '0 -14 48 28');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.setProperty('--burst-x', x.toFixed(1) + 'px');
    svg.style.setProperty('--burst-y', y.toFixed(1) + 'px');
    svg.style.setProperty('--burst-tx', driftX.toFixed(1) + 'px');
    svg.style.setProperty('--burst-ty', driftY.toFixed(1) + 'px');
    svg.style.setProperty('--burst-size', size + 'px');
    svg.style.setProperty('--burst-rotate', -28 + (index % 7) * 9 + 'deg');
    svg.style.setProperty('--burst-spin', spin + 'deg');
    svg.style.setProperty('--burst-delay', index * 18 + 'ms');
    svg.style.setProperty('--burst-duration', 680 + (index % 4) * 38 + 'ms');

    path.setAttribute('d', 'M0 0 C 12 -11 32 -12 46 -2 C 32 10 12 11 0 0 Z');
    svg.appendChild(path);
    return svg;
  }

  function burstLeaves(x, y) {
    if (!motionAllowed() || !Number.isFinite(x) || !Number.isFinite(y)) return;

    var now = Date.now();
    if (now - lastBurstAt < 280) return;
    lastBurstAt = now;

    var portal = ensureBurstPortal();
    var existing = portal.querySelectorAll('.leaf-burst__leaf');
    for (var e = 0; e < existing.length; e++) existing[e].remove();

    var count = smallScreen.matches ? 8 : 10;
    var fragment = document.createDocumentFragment();

    for (var i = 0; i < count; i++) {
      var leaf = createBurstLeaf(i, x, y, count);

      leaf.addEventListener(
        'animationend',
        function (event) {
          var particle = event.currentTarget;
          if (particle && particle.parentNode) particle.parentNode.removeChild(particle);
        },
        { once: true }
      );

      fragment.appendChild(leaf);
    }

    portal.appendChild(fragment);

    window.setTimeout(function () {
      var stale = portal.querySelectorAll('.leaf-burst__leaf');
      for (var j = 0; j < stale.length; j++) stale[j].remove();
    }, 1200);
  }

  /* ----------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------- */

  function init(scope) {
    var root = scope || document;

    initSplit(root);
    initReveals(root);
    initParallax(root);
    initVines(root);
    initCanopy(root);
    requestMeasure();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-run inside the theme editor when a section is added or updated.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  reduceQuery.addEventListener('change', function () {
    init(document);
  });

  window.PlantzMotion = { init: init, refresh: requestMeasure, burst: burstLeaves };
})();
